import { Suspense, lazy, type ComponentType, type ReactNode } from "react";
import { Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";
import { AuthBootstrap } from "@/features/auth/AuthBootstrap";
import {
  ProtectedRoute,
  PublicOnlyRoute,
  WorkspaceReadyRoute,
} from "@/features/auth/ProtectedRoute";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { LoginPage } from "@/pages/LoginPage";
import { WelcomePage } from "@/pages/WelcomePage";
import { ConnectExtensionPage } from "@/pages/ConnectExtensionPage";
import { MaintenancePage } from "@/pages/MaintenancePage";
import { MaintenanceGate } from "@/features/maintenance/MaintenanceGate";
import { ProfileNamePage } from "@/pages/ProfileNamePage";
import { TemplateShell } from "@/components/TemplateShell";
import { useI18n } from "@/i18n/I18nContext";
import { Spinner } from "@/shared/ui/spinner";

/**
 * A route component loaded on first use. When the chunk is gone after a deploy, the
 * vite:preloadError handler starts a reload and cancels the error; Vite then resolves the import
 * to undefined. Waiting forever keeps the loading state on screen until the reload replaces the
 * page, instead of crashing into the router's error screen.
 */
function lazyPage<M>(load: () => Promise<M | undefined>, pick: (module: M) => ComponentType) {
  return lazy(() => load().then((module) => (module ? { default: pick(module) } : new Promise<never>(() => {}))));
}

// The login page needs none of these, so they load when their route opens.
const OnboardingPage = lazyPage(() => import("@/pages/OnboardingPage"), (m) => m.OnboardingPage);
const MeetingsTemplatePage = lazyPage(() => import("@/pages/MeetingsTemplatePage"), (m) => m.MeetingsTemplatePage);
const MeetingTemplateDetailPage = lazyPage(() => import("@/pages/MeetingTemplateDetailPage"), (m) => m.MeetingTemplateDetailPage);
const SettingsTemplatePage = lazyPage(() => import("@/pages/SettingsTemplatePage"), (m) => m.SettingsTemplatePage);

function RouteLoading() {
  const { t } = useI18n();
  return <Spinner label={t("common.loading")} />;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

// Every route sits under the gate, so while maintenance closes the app the
// login page and all the others lead to the maintenance page, signed in or not.
const router = createBrowserRouter([
  {
    element: <MaintenanceGate />,
    children: [
      { path: "/maintenance", element: <MaintenancePage /> },
      {
        path: "/welcome",
        element: (
          <AuthBootstrap>
            <WelcomePage />
          </AuthBootstrap>
        ),
      },
      {
        path: "/login",
        element: (
          <AuthBootstrap>
            <PublicOnlyRoute><LoginPage /></PublicOnlyRoute>
          </AuthBootstrap>
        ),
      },
      {
        path: "/auth/verify",
        element: (
          <AuthBootstrap>
            <LoginPage />
          </AuthBootstrap>
        ),
      },
      {
        path: "/auth/connect-extension",
        element: (
          <AuthBootstrap>
            <ProtectedRoute><ConnectExtensionPage /></ProtectedRoute>
          </AuthBootstrap>
        ),
      },
      {
        path: "/setup/name",
        element: (
          <AuthBootstrap>
            <ProtectedRoute><ProfileNamePage /></ProtectedRoute>
          </AuthBootstrap>
        ),
      },
      {
        path: "/onboarding",
        element: (
          <AuthBootstrap>
            <ProtectedRoute><Lazy><OnboardingPage /></Lazy></ProtectedRoute>
          </AuthBootstrap>
        ),
      },
      {
        element: (
          <AuthBootstrap>
            <ProtectedRoute>
              <WorkspaceReadyRoute><TemplateShell /></WorkspaceReadyRoute>
            </ProtectedRoute>
          </AuthBootstrap>
        ),
        children: [
          { index: true, element: <Navigate to="/meetings" replace /> },
          { path: "/meetings", element: <Lazy><MeetingsTemplatePage /></Lazy> },
          { path: "/meetings/:id", element: <Lazy><MeetingTemplateDetailPage /></Lazy> },
          { path: "/settings", element: <Lazy><SettingsTemplatePage /></Lazy> },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
